import React, { useEffect, useState, useMemo, useCallback, useRef } from "react"
import "../style/ticketReply.css"
import { Tooltip } from "components/ui"
import { useSelector } from "react-redux"
import { orderBy } from "lodash"
import { inventoryStatusColor } from "utils/storeHube/statusColor"
import { useTranslation } from "react-i18next"
import { convertUrlToLink, withBotAvatar, isStoreHubBot } from "utils/storeHube/helpers"
import AvatarProfile from "components/ui/Avatar/AvatarProfile"
import { USER_SYS } from "constants/app.constant"
import classNames from "classnames"
import ReactHtmlParser from "react-html-parser"
import { Link, useNavigate, useLocation } from "react-router-dom"
import useQuery from "utils/hooks/useQuery"
import dayjs from "dayjs"
import relativeTimePlugin from "dayjs/plugin/relativeTime"
import "dayjs/locale/es"
import {
    LuRefreshCw,
    LuUserCheck,
    LuLink2,
    LuListChecks,
    LuMessageSquare,
    LuActivity,
} from "react-icons/lu"
import { HiOutlineSparkles } from "react-icons/hi"
import AttachmentPreviewDialog from "./AttachmentPreviewDialog"
import TicketAttachmentGallery from "./TicketAttachmentGallery"
import { cleanIsoDate } from "utils/storeHube/helpersDate"

dayjs.extend(relativeTimePlugin)

const TICKET_QUESTION = "ticket_question"
const TICKET_STATUS = "ticket_status"
const TICKET_REPLY = "ticket_reply"
const TICKET_OPERATOR = "ticket_operator"
const TICKET_LINKED = "ticket_linked"

const FILTER_ALL = "all"
const FILTER_COMMENTS = "comments"
const FILTER_SYSTEM = "system"

// Usamos cleanIsoDate (helpersDate) en vez de duplicar el regex. Antes existian
// dos copias del mismo `.replace(/\.\d+(Z|[+-]\d{2}:?\d{2}|$)/, "$1")` (una aca
// y otra en helpersDate.js) y un fix se podia perder en una sola de las dos.
const relTime = (raw) => (raw ? dayjs(cleanIsoDate(raw)).locale("es").fromNow() : "")
const absTime = (raw) => (raw ? dayjs(cleanIsoDate(raw)).locale("es").format("DD MMM YYYY · HH:mm") : "")

// Empty state cuando el feed esta vacio para el filtro activo.
const TimelineEmptyState = ({ filter, t }) => {
    const isCommentsFilter = filter === FILTER_COMMENTS
    const Icon = isCommentsFilter ? LuMessageSquare : LuActivity
    const title = isCommentsFilter
        ? t("no_comments_yet", "Sin comentarios todavía")
        : t("no_system_events", "Sin eventos del sistema")
    const subtitle = isCommentsFilter
        ? t("no_comments_hint", "Cuando alguien comente, aparecerá acá.")
        : t("no_system_events_hint", "Los cambios de estado y asignaciones se mostrarán aquí.")
    return (
        <div className="flex flex-col items-center justify-center text-center py-12 px-6 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 shadow-sm mb-4">
                <Icon className="w-5 h-5 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
            </div>
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 leading-snug">
                {title}
            </p>
            <p className="text-[12px] leading-relaxed text-gray-500 dark:text-gray-400 mt-2 max-w-[280px]">
                {subtitle}
            </p>
        </div>
    )
}

const EVENT_META = {
    [TICKET_STATUS]: {
        icon: LuRefreshCw,
        bg: "bg-indigo-50 dark:bg-indigo-900/25",
        fg: "text-indigo-600 dark:text-indigo-300",
    },
    [TICKET_OPERATOR]: {
        icon: LuUserCheck,
        bg: "bg-violet-50 dark:bg-violet-900/25",
        fg: "text-violet-600 dark:text-violet-300",
    },
    [TICKET_LINKED]: {
        icon: LuLink2,
        bg: "bg-sky-50 dark:bg-sky-900/25",
        fg: "text-sky-600 dark:text-sky-300",
    },
    [TICKET_QUESTION]: {
        icon: LuListChecks,
        bg: "bg-amber-50 dark:bg-amber-900/25",
        fg: "text-amber-600 dark:text-amber-300",
    },
}

const CREATED_META = {
    icon: HiOutlineSparkles,
    bg: "bg-emerald-50 dark:bg-emerald-900/25",
    fg: "text-emerald-600 dark:text-emerald-300",
}

// Todos los marcadores miden exactamente 32 × 32 px
const MARKER_PX = 32
// El rail se centra horizontalmente en el marcador
const RAIL_LEFT_PX = MARKER_PX / 2 - 1 // 15px (rail de 2px → offset para centrarlo)

const PersonInline = ({ user }) => {
    if (!user) return null
    return (
        <span className="font-semibold text-gray-900 dark:text-gray-100">
            {user?.name} {user?.lastname}
        </span>
    )
}

const StatusInline = ({ statusId }) => {
    const meta = inventoryStatusColor[statusId]
    if (!meta) return null
    return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium leading-tight bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 align-middle mx-0.5">
            <span className={classNames("w-1.5 h-1.5 rounded-full", meta.dotClass)} />
            {meta.label}
        </span>
    )
}

const SystemEventBody = ({ activity, t }) => {
    const user = activity?.user || USER_SYS
    const userName = `${user?.name || ""} ${user?.lastname || ""}`.trim()
    const ticketAlias = (t("ticket_alias") || "tarea").toLowerCase()

    let body = null
    switch (activity.type) {
        case TICKET_STATUS:
            if (activity.ticket_status_id === 1) {
                body = activity?.ticket_operator ? (
                    <>
                        <span>{` ${t("hasCreatedAndAssigned")} ${ticketAlias} ${t("a")} `}</span>
                        <PersonInline user={activity.ticket_operator} />
                    </>
                ) : (
                    <span>{` ${t("hasCreatedTicket")} ${ticketAlias}`}</span>
                )
            } else {
                body = (
                    <>
                        <span>{` ${t("hasChangedState")} `}</span>
                        <StatusInline statusId={activity.ticket_status_id} />
                    </>
                )
            }
            break
        case TICKET_OPERATOR:
            body = (
                <>
                    <span>{` ${t("hasTicketAssigned")} ${ticketAlias} ${t("a")} `}</span>
                    <PersonInline user={activity?.ticket_operator} />
                </>
            )
            break
        case TICKET_LINKED:
            body = (
                <>
                    <span>{` ${t("hasLinkedTicket")} ${ticketAlias} `}</span>
                    <Link
                        to={`/tickets/inbox?ticket=${activity?.linked_ticket}`}
                        className="font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 hover:underline"
                    >
                        #{activity?.linked_ticket}
                    </Link>
                </>
            )
            break
        case TICKET_QUESTION:
            body = (
                <>
                    <span>{` ha respondido el cuestionario `}</span>
                    <span className="font-medium text-gray-800 dark:text-gray-100 capitalize">
                        {activity?.question_group?.name}
                    </span>
                </>
            )
            break
        default:
            body = null
    }

    return (
        <div className="text-[13.5px] text-gray-600 dark:text-gray-400 py-1" style={{ lineHeight: 1.6 }}>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{userName}</span>
            {body}
            <Tooltip title={absTime(activity.created_at)}>
                <span className="text-[12px] text-gray-400 dark:text-gray-500 whitespace-nowrap cursor-default ml-1">
                    · {relTime(activity.created_at)}
                </span>
            </Tooltip>
        </div>
    )
}

// Detecta el patron de "respuesta a una incidencia desde el cuestionario".
// Cubre 2 formatos:
//  - NUEVO (post 2026-05-19): wrapper con `data-incidence-reply="1"` y clases
//    `.incidence-question` / `.incidence-answer`.
//  - LEGACY: `<p style="font-weight: bold; color: #4B5563">PREGUNTA</p><p>RESPUESTA</p>`
//    emitido por `formatComment` historico en TicketAnswers.js. Es 100% deterministico
//    (el color/peso/orden son hardcoded), no hay riesgo de falso positivo con
//    comentarios libres.
//
// Defense in depth XSS (Pase 27): textContent en vez de innerHTML/outerHTML
// para que cualquier HTML peligroso embebido en el reply (legacy sin escape
// o ataque manual via API) llegue como texto plano. React escapa al render.
const parseIncidenceReply = (rawHtml) => {
    if (!rawHtml || typeof rawHtml !== 'string') return null
    try {
        const doc = new DOMParser().parseFromString(rawHtml, "text/html")

        // Formato nuevo
        const wrapper = doc.querySelector("[data-incidence-reply]")
        if (wrapper) {
            const questionEl = wrapper.querySelector(".incidence-question")
            const answerEl = wrapper.querySelector(".incidence-answer")
            if (questionEl && answerEl) {
                return {
                    question: questionEl.textContent.trim(),
                    answer: answerEl.textContent.trim(),
                }
            }
        }

        // Formato legacy: primer <p> bold con color #4B5563 + segundo <p>
        const paragraphs = doc.body.querySelectorAll(":scope > p")
        if (paragraphs.length >= 2) {
            const first = paragraphs[0]
            const style = (first.getAttribute("style") || "").toLowerCase().replace(/\s+/g, "")
            const isBoldQuestion =
                style.includes("font-weight:bold") &&
                (style.includes("color:#4b5563") || style.includes("color:rgb(75,85,99)"))
            if (isBoldQuestion) {
                const rest = Array.from(paragraphs).slice(1).map(p => p.textContent).join("\n").trim()
                return { question: first.textContent.trim(), answer: rest }
            }
        }

        return null
    } catch {
        return null
    }
}

const CommentBody = ({ activity, t, onPreviewImage }) => {
    const userName = activity?.user
        ? `${activity.user.name} ${activity.user.lastname}`
        : t("userNotFound")

    const hasSingleFile = !!activity?.file
    const hasMultiFiles = !hasSingleFile && activity?.ticket_reply_attachment?.length > 0

    const allAttachments = hasSingleFile
        ? [activity]
        : hasMultiFiles
            ? activity.ticket_reply_attachment
            : []

    const incidenceReply = parseIncidenceReply(activity?.message)

    return (
        <div>
            <div className="flex items-baseline gap-1.5 mb-2 flex-wrap">
                <span className="text-[14px] font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {userName}
                </span>
                <span className="text-[13px] text-gray-500 dark:text-gray-400">
                    {incidenceReply
                        ? t("answered_incidence", "respondió una incidencia")
                        : t("commented", "comentó")}
                </span>
                <Tooltip title={absTime(activity.created_at)}>
                    <span className="text-[12px] text-gray-400 dark:text-gray-500 whitespace-nowrap cursor-default">
                        · {relTime(activity.created_at)}
                    </span>
                </Tooltip>
            </div>
            {incidenceReply ? (
                <div className="rounded-lg border border-indigo-100 dark:border-indigo-900/60 bg-indigo-50/40 dark:bg-indigo-950/20 overflow-hidden">
                    <div className="px-3.5 py-2 border-b border-indigo-100 dark:border-indigo-900/60 bg-white/60 dark:bg-transparent">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-indigo-600 dark:text-indigo-400 mb-0.5">
                            {t("incidence_question", "Pregunta")}
                        </div>
                        <div className="text-[14px] font-medium text-gray-900 dark:text-gray-100 leading-snug">
                            {incidenceReply.question}
                        </div>
                    </div>
                    <div className="px-3.5 py-2.5">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">
                            {t("incidence_answer", "Respuesta")}
                        </div>
                        {incidenceReply.answer?.length > 0 && (
                            <div
                                className="text-[14px] text-gray-800 dark:text-gray-200 break-words whitespace-pre-wrap"
                                style={{ lineHeight: 1.55 }}
                            >
                                {incidenceReply.answer}
                            </div>
                        )}
                        {allAttachments.length > 0 && (
                            <div className={incidenceReply.answer?.length > 0 ? 'mt-2' : 'mt-1'}>
                                <TicketAttachmentGallery
                                    attachments={allAttachments}
                                    onPreviewImage={onPreviewImage}
                                />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <div
                        className="ticketReply ql-editor !p-0 text-[14px] text-gray-800 dark:text-gray-200 break-words"
                        style={{ lineHeight: 1.65 }}
                    >
                        {ReactHtmlParser(convertUrlToLink(activity.message))}
                    </div>
                    <TicketAttachmentGallery attachments={allAttachments} onPreviewImage={onPreviewImage} />
                </>
            )}
        </div>
    )
}

/*
 * ActivityItem — un nodo del feed de actividad.
 *
 * El rail se dibuja como un div hermano del marker, posicionado con
 * `position: absolute` en inline style (no Tailwind arbitrary values)
 * para garantizar que el JIT no necesite purgarlo.
 * Empieza al borde inferior del marker (top: MARKER_PX px) y llega
 * hasta el fondo del li, que tiene pb-7 (28px) de separación entre items.
 * El último item no muestra rail.
 */
const ActivityItem = React.memo(({ activity, isLast, t, onPreviewImage, pulsoResaltado }) => {
    const isComment = activity.type === TICKET_REPLY
    const isCreated = activity.type === TICKET_STATUS && activity.ticket_status_id === 1
    const isBot = isStoreHubBot(activity?.user)
    const meta = isCreated
        ? CREATED_META
        : EVENT_META[activity.type] || EVENT_META[TICKET_STATUS]
    const Icon = meta.icon
    // El badge encima del avatar solo se muestra para usuarios humanos.
    // Para el StoreHub Bot, el avatar (logo) ya identifica claramente la fuente.
    const showBadge = !isComment && !isBot

    return (
        <li
            // Ancla para bajar hasta un comentario puntual (`?reply=`). Solo en
            // comentarios: el feed mezcla tres tablas distintas y un ticket_log
            // puede tener el mismo id que un ticket_reply, asi que un ancla
            // generica apuntaria al evento equivocado.
            data-reply-id={isComment ? activity.id : undefined}
            className="relative flex gap-4"
            // Separación generosa entre eventos: 28px inferior en todos menos el último
            style={{ paddingBottom: isLast ? 0 : 28 }}
        >
            {/* Rail vertical — CSS inline para evitar dependencia de JIT arbitrary values */}
            {!isLast && (
                <div
                    aria-hidden="true"
                    style={{
                        position:   'absolute',
                        left:       RAIL_LEFT_PX,
                        top:        MARKER_PX,   // arranca justo debajo del marcador
                        bottom:     0,
                        width:      2,
                        background: 'var(--timeline-rail, #e5e7eb)', // CSS var con fallback gray-200
                        borderRadius: 1,
                        pointerEvents: 'none',
                    }}
                />
            )}

            {/* Marcador 32×32 — avatar real (incluye logo de StoreHub Bot).
                Para eventos del sistema disparados por usuarios humanos, sumamos
                un mini-badge abajo-derecha con el ícono del tipo de evento.
                Para el StoreHub Bot, el avatar (logo) ya identifica la fuente,
                así que no agregamos badge extra. */}
            <div
                className="relative z-10 flex-shrink-0"
                style={{ width: MARKER_PX, height: MARKER_PX }}
            >
                <AvatarProfile
                    size={MARKER_PX}
                    user={withBotAvatar(activity?.user) || USER_SYS}
                />
                {showBadge && (
                    <div
                        className={classNames(
                            "absolute rounded-full flex items-center justify-center ring-2 ring-white dark:ring-gray-800",
                            meta.bg,
                            meta.fg
                        )}
                        style={{
                            width: 16,
                            height: 16,
                            right: -2,
                            bottom: -2,
                        }}
                        aria-hidden="true"
                    >
                        <Icon style={{ width: 10, height: 10, flexShrink: 0, strokeWidth: 2 }} />
                    </div>
                )}
            </div>

            {/* Contenido — alineado al top del marcador con leve offset para baseline visual */}
            <div className="relative isolate flex-1 min-w-0 pt-1">
                {/* El resalte es una capa hermana y no una clase sobre el contenido:
                    para reiniciar @keyframes hay que remontar el elemento, y remontar
                    el contenido se lleva puesto el foco y el estado de la galeria de
                    adjuntos si el usuario esta interactuando justo en ese momento.
                    La key con el pulso hace que un segundo click vuelva a animar. */}
                {pulsoResaltado ? (
                    <span
                        key={`resaltado-${pulsoResaltado}`}
                        className="reply-highlight"
                        aria-hidden="true"
                    />
                ) : null}
                {isComment ? (
                    <CommentBody activity={activity} t={t} onPreviewImage={onPreviewImage} />
                ) : (
                    <SystemEventBody activity={activity} t={t} />
                )}
            </div>
        </li>
    )
})

const TicketTimeline = ({ props }) => {
    const {
        ticket_log = [],
        ticket_reply = [],
        ticket_answer = [],
    } = props

    const { selectedTicket, loadingSelectedTicket } = useSelector(
        (state) => state.ticketList.data
    )
    const { t } = useTranslation()

    const [activities, setActivities] = useState([])
    const [filter, setFilter] = useState(FILTER_ALL)
    const [previewFile, setPreviewFile] = useState(null)

    // `?reply=` viene de la campanita: dice a que comentario bajar dentro del feed.
    const query = useQuery()
    const navigate = useNavigate()
    const location = useLocation()
    const replyAncla = query.get("reply")
    const [resaltado, setResaltado] = useState(null)
    const raizRef = useRef(null)

    const getTypeLog = (item) => {
        if (item.message)                                                  return { ...item, type: TICKET_REPLY    }
        if (item.question_group)                                           return { ...item, type: TICKET_QUESTION }
        if (item.ticket_operator_id && item.ticket_status_id !== 1)        return { ...item, type: TICKET_OPERATOR }
        if (item.linked_ticket)                                            return { ...item, type: TICKET_LINKED   }
        return { ...item, type: TICKET_STATUS }
    }

    useEffect(() => {
        const merged = [...ticket_log, ...ticket_reply, ...ticket_answer].map(getTypeLog)
        setActivities(orderBy(merged, ["created_at"], ["desc"]))
    }, [ticket_log, ticket_reply, ticket_answer])

    const counts = useMemo(
        () => ({
            [FILTER_ALL]:      activities.length,
            [FILTER_COMMENTS]: activities.filter((a) => a.type === TICKET_REPLY).length,
            [FILTER_SYSTEM]:   activities.filter((a) => a.type !== TICKET_REPLY).length,
        }),
        [activities]
    )

    // Memoizado para no re-filtrar el array (potencialmente cientos de
    // entradas) en cada render del padre o de los toggles UI no relacionados.
    const visibleActivities = useMemo(
        () => activities.filter((a) => {
            if (filter === FILTER_COMMENTS) return a.type === TICKET_REPLY
            if (filter === FILTER_SYSTEM)   return a.type !== TICKET_REPLY
            return true
        }),
        [activities, filter]
    )

    // El comentario al que apunta el ancla existe en el feed, sin importar el filtro.
    const anclaEnElFeed = useMemo(
        () =>
            !!replyAncla &&
            activities.some(
                (a) => a.type === TICKET_REPLY && String(a.id) === String(replyAncla)
            ),
        [replyAncla, activities]
    )

    // Si el filtro activo es "Sistema", el comentario no esta renderizado y no hay
    // nada que resaltar. Venir de una mencion gana sobre el filtro elegido antes.
    useEffect(() => {
        if (anclaEnElFeed && filter === FILTER_SYSTEM) {
            setFilter(FILTER_ALL)
        }
    }, [anclaEnElFeed, filter])

    // Saca el `reply` de la URL una vez usado, para que volver a clickear la misma
    // notificacion dispare la animacion de nuevo en vez de no hacer nada.
    const consumirAncla = useCallback(() => {
        const params = new URLSearchParams(location.search)
        params.delete("reply")
        const qs = params.toString()
        navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true })
    }, [location.pathname, location.search, navigate])

    useEffect(() => {
        if (!replyAncla) return

        // Acotado al feed propio y no por getElementById: el detalle puede estar
        // montado dos veces (vista y dialog) y una busqueda global agarraria el
        // comentario del otro, scrolleando donde el usuario no esta mirando.
        const el = raizRef.current?.querySelector(
            `[data-reply-id="${CSS.escape(String(replyAncla))}"]`
        )
        // Todavia no esta en el DOM (el hallazgo sigue cargando, o el filtro lo
        // esconde). El efecto vuelve a correr cuando cambie `visibleActivities`.
        if (!el) return

        // El detalle hace window.scrollTo(top) al cambiar de hallazgo. Ese scroll y
        // este compiten en el mismo ciclo, asi que este va despues del layout.
        // Nota: con el dialog abierto sobre el inbox el detalle queda montado dos
        // veces, y las dos instancias scrollearian su propio feed. Se deja asi a
        // proposito. Para llegar aca con el dialog abierto habria que clickear la
        // campanita del header, y el overlay del modal la tapa. Un desempate por
        // `[role="dialog"]` es peor que el problema: detecta "hay algun modal", no
        // cual contiene esta vista, asi que cualquier otro modal abierto (el preview
        // de adjuntos, sin ir mas lejos) dejaba el ancla sin procesar para siempre.
        const timer = setTimeout(() => {
            const sinMovimiento =
                typeof window !== "undefined" &&
                window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches

            el.scrollIntoView({
                behavior: sinMovimiento ? "auto" : "smooth",
                block: "center",
            })
            // El pulso sube en cada click aunque sea el mismo comentario. Con un id
            // pelado, React no re-renderiza (mismo valor), la animacion no reinicia
            // y el apagado sigue contando desde el click anterior.
            setResaltado((prev) => ({
                id: String(replyAncla),
                pulso: (prev?.pulso ?? 0) + 1,
            }))
            consumirAncla()
        }, 80)

        return () => clearTimeout(timer)
    }, [replyAncla, visibleActivities, consumirAncla])

    // Un ancla que no se resuelve nunca (comentario borrado, o de otro hallazgo) no
    // puede quedar pegada en la URL: sin esto, cada recarga vuelve a intentarla y el
    // parametro viaja en cualquier link que el usuario copie. La gracia arranca
    // recien cuando termino de cargar, asi no le gana al scroll de un caso normal.
    useEffect(() => {
        if (!replyAncla || loadingSelectedTicket) return
        const timer = setTimeout(consumirAncla, 3000)
        return () => clearTimeout(timer)
    }, [replyAncla, loadingSelectedTicket, consumirAncla])

    // El resaltado se apaga solo. Va en su propio efecto para que consumir el
    // parametro de la URL no cancele el timer apenas se prende. Como el objeto es
    // nuevo en cada pulso, el cleanup reinicia la cuenta en vez de arrastrar la vieja.
    useEffect(() => {
        if (!resaltado) return
        const timer = setTimeout(() => setResaltado(null), 2400)
        return () => clearTimeout(timer)
    }, [resaltado])

    const tabBase     = "px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5"
    const tabActive   = "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-300 shadow-sm"
    const tabInactive = "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"

    const tabs = [
        { key: FILTER_ALL,      label: t("all")      || "Todo"        },
        { key: FILTER_COMMENTS, label: t("comments") || "Comentarios" },
        { key: FILTER_SYSTEM,   label: t("system")   || "Sistema"     },
    ]

    return (
        <>
            <div id="replies" ref={raizRef} className="ticket-timeline-root my-4">
                {/* Tabs de filtro: centradas en mobile, alineadas a la
                    izquierda en desktop. */}
                <div className="mb-5 flex justify-center md:justify-start">
                <div
                    role="tablist"
                    aria-label={t("activity_filter", "Filtrar actividad")}
                    className="inline-flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-full"
                >
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={filter === tab.key}
                            aria-pressed={filter === tab.key}
                            onClick={() => setFilter(tab.key)}
                            className={classNames(tabBase, filter === tab.key ? tabActive : tabInactive)}
                        >
                            <span>{tab.label}</span>
                            <span
                                className={classNames(
                                    "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-semibold leading-none",
                                    filter === tab.key
                                        ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
                                        : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                )}
                            >
                                {counts[tab.key]}
                            </span>
                        </button>
                    ))}
                </div>
                </div>

                {visibleActivities.length > 0 ? (
                    <ol aria-label={t("activity_feed", "Actividad")}>
                        {visibleActivities.map((activity, idx) => (
                            <ActivityItem
                                // El tipo va en la key porque el feed mezcla tres
                                // tablas y un ticket_log puede compartir id con un
                                // ticket_reply: con la key pelada React reconcilia
                                // dos hermanos distintos como si fueran el mismo.
                                key={`${activity.type}-${activity.id}`}
                                activity={activity}
                                isLast={idx === visibleActivities.length - 1}
                                t={t}
                                onPreviewImage={setPreviewFile}
                                pulsoResaltado={
                                    activity.type === TICKET_REPLY &&
                                    String(activity.id) === resaltado?.id
                                        ? resaltado.pulso
                                        : null
                                }
                            />
                        ))}
                    </ol>
                ) : (
                    <TimelineEmptyState filter={filter} t={t} />
                )}
            </div>

            <AttachmentPreviewDialog
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />
        </>
    )
}

export default TicketTimeline
